import { EntityLink as Link } from '../../navigation/navigation'
import { useListState } from '../../navigation/useListState'
import React, { useState, useEffect } from 'react'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { Input, Select, Button, Pagination } from '../../components/ui'
import { getContractStatusClass } from '../../utils/contractStatus'
import { CONTRACT_STATUSES } from '../../config/contractStatusWorkflow'
import { buildStageTypeFilterOptions } from './stageTypeFilterOptions'
import './Company.css'

function Contracts() {
  const { filters, setFilters, pagination, setPagination, search, setSearch } = useListState({
    filters: {
      client_id: '',
      status: '',
      stage_type: '',
      debt: ''
    },
    pagination: {
      page: 1,
      limit: 50,
      total: 0,
      pages: 1
    },
    search: '',
  })
  const navigate = useNavigate()
  const { user } = useAuth()
  const canCreate = user?.permissions?.['contracts.create']
  const [contracts, setContracts] = useState([])
  const [clients, setClients] = useState([])
  const [stageTypes, setStageTypes] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)

  const [suggestions, setSuggestions] = useState([])

  const effectiveSearch = (search || '').trim().length >= 3 ? (search || '').trim() : ''
  const stageTypeOptions = React.useMemo(() => buildStageTypeFilterOptions(stageTypes), [stageTypes])

  useEffect(() => {
    fetchClients()
    fetchStageTypes()
    fetchReferences()
  }, [])

  const fetchReferences = async () => {
    try {
      const res = await api.get('/admin/reference')
      const refs = res.data?.references || {}
      const refStatuses = refs['статусы_договоров'] || []
      const normalizedRefStatuses = new Set(refStatuses.map((item) => String(item?.name || item?.code || item?.id || item).toLowerCase().trim()))
      setStatusOptions(CONTRACT_STATUSES.map((status) => {
        const refItem = refStatuses.find((item) => String(item?.name || item?.code || item?.id || item).toLowerCase().trim() === status)
        return refItem || { id: status, name: status }
      }).filter((item) => normalizedRefStatuses.size === 0 || CONTRACT_STATUSES.includes(String(item?.name || item?.code || item?.id || item).toLowerCase().trim())))
    } catch {
      setStatusOptions(CONTRACT_STATUSES.map((status) => ({ id: status, name: status })))
    }
  }

  useEffect(() => {
    fetchContracts()
  }, [filters.client_id, filters.status, filters.stage_type, filters.debt, pagination.page, effectiveSearch])

  const fetchSuggestions = React.useCallback(async (q) => {
    const query = (q || '').trim()
    if (query.length < 3) {
      setSuggestions([])
      return
    }
    try {
      const res = await api.get(`/company/contracts/suggest?q=${encodeURIComponent(query)}&limit=10`)
      setSuggestions(res.data?.suggestions || [])
    } catch {
      setSuggestions([])
    }
  }, [])

  useEffect(() => {
    if (!search || search.trim().length < 3) {
      setSuggestions([])
      return
    }
    const t = setTimeout(() => fetchSuggestions(search), 300)
    return () => clearTimeout(t)
  }, [search, fetchSuggestions])

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchClients = async () => {
    try {
      const response = await api.get('/company/clients?limit=1000')
      if (response.data && response.data.clients) {
        setClients(response.data.clients)
      }
    } catch (error) {
      console.error('Error fetching clients:', error)
    }
  }

  const fetchStageTypes = async () => {
    try {
      const response = await api.get('/company/stage-types')
      if (response.data && response.data.stage_types) {
        setStageTypes(response.data.stage_types)
      }
    } catch (error) {
      console.error('Error fetching stage types:', error)
    }
  }

  const applySuggestion = (contract) => {
    setSearch(contract.number || '')
    setSuggestions([])
    const hadEffective = effectiveSearch.length > 0
    if (!hadEffective) setPagination(prev => ({ ...prev, page: 1 }))
  }

  const fetchContracts = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filters.client_id) params.append('client_id', filters.client_id)
      if (filters.status) params.append('status', filters.status)
      if (filters.stage_type) params.append('stage_type', filters.stage_type)
      if (filters.debt) params.append('debt', filters.debt)
      if (effectiveSearch) params.append('search', effectiveSearch)
      params.append('page', pagination.page.toString())
      params.append('limit', pagination.limit.toString())

      const response = await api.get(`/company/contracts?${params}`)
      
      if (response.data) {
        if (response.data.contracts) {
          setContracts(response.data.contracts)
          setPagination(prev => ({
            ...prev,
            total: response.data.total || 0,
            pages: response.data.pages || 1
          }))
        } else {
          setContracts([])
        }
      } else {
        setContracts([])
      }
    } catch (error) {
      console.error('Error fetching contracts:', error)
      console.error('Error details:', error.response?.data || error.message)
      setContracts([])
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleSearchChange = (value) => {
    const hadEffective = (search || '').trim().length >= 3
    const hasEffective = (value || '').trim().length >= 3
    setSearch(value)
    if (hadEffective !== hasEffective) setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU')
  }

  const formatAmount = (amount) => {
    if (!amount) return '-'
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount)
  }

  usePageHeader({
    renderActions: () => canCreate ? (
      <Button variant="primary" onClick={() => navigate('/company/contracts/new')}>+ Создать договор</Button>
    ) : null
  }, [canCreate])

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  return (
    <div className="company-page">
      <div className="filters-section">
        <div className="filters">
          <div className="filter-group">
            <label>Клиент</label>
            <Select
              value={filters.client_id}
              onChange={(e) => handleFilterChange('client_id', e.target.value)}
              aria-label="Клиент"
            >
              <option value="">Все клиенты</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="filter-group">
            <label>Статус</label>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              aria-label="Статус"
            >
              <option value="">Все статусы</option>
              {statusOptions.map((opt) => (
                <option key={opt.id} value={opt.name || opt.code || opt.id}>
                  {opt.name || opt.code || opt.id}
                </option>
              ))}
            </Select>
          </div>
          <div className="filter-group">
            <label htmlFor="contracts-stage-type-filter">Тип стадии</label>
            <Select
              id="contracts-stage-type-filter"
              className="stage-type-filter-select"
              value={filters.stage_type}
              onChange={(event) => handleFilterChange('stage_type', event.target.value)}
            >
              <option value="">Все типы стадий</option>
              {stageTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Select>
          </div>
          <div className="filter-group">
            <label>Задолженность</label>
            <Select
              value={filters.debt}
              onChange={(e) => handleFilterChange('debt', e.target.value)}
              aria-label="Задолженность"
            >
              <option value="">Все</option>
              <option value="true">Только с задолженностью</option>
              <option value="false">Без задолженности</option>
            </Select>
          </div>
          <div className="filter-group filter-group-search">
            <label>Поиск по номеру</label>
            <div style={{ position: 'relative' }}>
              <Input
                type="text"
                placeholder="Поиск по номеру договора"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => search && search.trim().length >= 3 && fetchSuggestions(search)}
                aria-label="Поиск по номеру договора"
                aria-autocomplete="list"
                aria-controls="contract-search-suggestions"
              />
              {suggestions.length > 0 && (
                <ul id="contract-search-suggestions" className="search-suggestions" role="listbox">
                  {suggestions.map((s) => (
                    <li
                      key={s.id}
                      role="option"
                      tabIndex={0}
                      onMouseDown={(e) => { e.preventDefault(); applySuggestion(s) }}
                      className="search-suggestion-item"
                    >
                      <span className="search-suggestion-name">{s.number || '-'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="table-section">
        {contracts.length === 0 ? (
          <div className="no-data">Договоры не найдены</div>
        ) : (
          <>
            {isMobile ? (
              <>
              <div className="list-cards">
                {contracts.map((contract) => (
                  <div
                    key={contract.id}
                    className="list-card ui-card ui-card-hover"
                    onClick={() => navigate(`/contract/${contract.id}`)}
                  >
                    <div className="list-card-header">
                      <Link to={`/contract/${contract.id}`} className="list-card-title">{contract.number || 'Договор'}</Link>
                    </div>
                    <div className="list-card-body">
                      <div className="list-card-row">
                        <span className="list-card-label">Клиент:</span>
                        <Link className={`list-card-value${contract.client_id ? ' clickable-link' : ''}`} to={contract.client_id ? `/client/${contract.client_id}` : undefined}
                        >
                          {contract.client_name || '-'}
                        </Link>
                      </div>
                      <div className="list-card-row">
                        <span className="list-card-label">Объект:</span>
                        <Link className={`list-card-value${contract.object_id ? ' clickable-link' : ''}`} to={contract.object_id ? `/object/${contract.object_id}` : undefined}
                        >
                          {contract.object_name || '-'}
                        </Link>
                      </div>
                      <div className="list-card-row">
                        <span className="list-card-label">Дата:</span>
                        <span className="list-card-value">{formatDate(contract.contract_date)}</span>
                      </div>
                      <div className="list-card-row">
                        <span className="list-card-label">Сумма:</span>
                        <span className="list-card-value mono">{formatAmount(contract.total_amount)}</span>
                      </div>
                      <div className="list-card-row">
                        <span className="list-card-label">Статус:</span>
                        <span className={`list-card-value object-status-badge ${getContractStatusClass(contract.status)}`}>
                          {contract.status || '-'}
                        </span>
                      </div>
                      <div className="list-card-row">
                        <span className="list-card-label">Стадий / Актов:</span>
                        <span className="list-card-value mono">{contract.stages_count ?? '-'} / {contract.acts_count ?? '-'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
                {pagination.total > 0 && (
                  <div className="company-pagination-bar-wrap">
                    <Pagination
                      page={pagination.page}
                      pages={pagination.pages}
                      total={pagination.total}
                      pageSize={pagination.limit}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="company-table-frame">
              <div className="table-responsive">
                <table className="ui-table data-table">
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th>Клиент</th>
                      <th>Объект</th>
                      <th className="text-right">Дата</th>
                      <th className="text-right">Сумма</th>
                      <th>Статус</th>
                      <th className="text-right">Стадий</th>
                      <th className="text-right">Актов</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract) => (
                      <tr
                        key={contract.id}
                        className="clickable-row"
                        onClick={() => navigate(`/contract/${contract.id}`)}
                      >
                        <td>
                          <Link to={`/contract/${contract.id}`} className="list-primary-name">{contract.number || '-'}</Link>
                        </td>
                        <td
                          onClick={(e) => {
                            e.stopPropagation()
                            if (contract.client_id) navigate(`/client/${contract.client_id}`)
                          }}
                        >
                          {contract.client_id ? (
                            <Link className="ui-table-entity-link" to={contract.client_id ? `/client/${contract.client_id}` : undefined}>{contract.client_name || '-'}</Link>
                          ) : (
                            contract.client_name || '-'
                          )}
                        </td>
                        <td
                          onClick={(e) => {
                            e.stopPropagation()
                            if (contract.object_id) navigate(`/object/${contract.object_id}`)
                          }}
                        >
                          {contract.object_id ? (
                            <Link className="ui-table-entity-link" to={contract.object_id ? `/object/${contract.object_id}` : undefined}>{contract.object_name || '-'}</Link>
                          ) : (
                            contract.object_name || '-'
                          )}
                        </td>
                        <td className="text-right mono">{formatDate(contract.contract_date)}</td>
                        <td className="text-right mono">{formatAmount(contract.total_amount)}</td>
                        <td>
                          <span className={`object-status-badge ${getContractStatusClass(contract.status)}`}>
                            {contract.status || '-'}
                          </span>
                        </td>
                        <td className="text-right mono">{contract.stages_count || 0}</td>
                        <td className="text-right mono">{contract.acts_count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                {pagination.total > 0 && (
                  <Pagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    pageSize={pagination.limit}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Contracts
